import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { registerUser } from "../../apis/apisList/userApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../../Context/AppContext";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const SignupForm = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [searchParams] = useSearchParams();
  const redirectPage = searchParams.get("redirect");
  const navigate = useNavigate(); // Initialize navigate for navigation
  const { login } = useApp();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required.";
    }
    if (!email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address.";
    }
    if (!companyName.trim()) {
      newErrors.companyName = "Company name is required.";
    }
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (!/^\d{10}$/.test(phoneNumber)) {
      newErrors.phoneNumber = "Enter a valid 10-digit phone number.";
    }
    if (!password.trim()) {
      newErrors.password = "Password is required.";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
    }
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (confirmPassword !== password) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async () => {
    if (validateForm()) {
      setLoading(true); // Start loading
      setSuccessMessage(""); // Clear previous messages
      setErrorMessage(""); // Clear previous error messages

      // Send the POST request to register the user
      try {
        const response = await registerUser({
          fullName,
          email: email.toLowerCase(), // Ensure email is in lowercase
          password,
          confirmPassword,
          company: companyName,
          phone: phoneNumber,
        });
        toast.success("Verification Code sent to your email");
        // login(response);
        const redirect = redirectPage
          ? `/verification?redirect=${encodeURIComponent(redirectPage)}`
          : "/verification";
        localStorage.setItem("temp_user", email);
        navigate(redirect);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "An error occurred. Please try again.";
        setErrorMessage(message);
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSignup();
    }
  };
  const signup_style = {
    title: {
      fontSize: "24px",
      fontWeight: "600",
    },
    signupBtn: {
      backgroundColor: "#FF8332",
      boxShadow: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      fontSize: "20px",
      fontWeight: "500",
      textTransform: "inherit",
    },
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "355px" }}>
      <Typography
        style={signup_style.title}
        variant="h6"
        align="center"
        sx={{ marginBottom: 4, paddingBottom: 2, borderBottom: 2 }}
      >
        Register to access your projects, quotes and flight data
      </Typography>

      {successMessage && (
        <Typography sx={{ color: "green", marginBottom: 2 }}>
          {successMessage}
        </Typography>
      )}
      {errorMessage && (
        <Typography sx={{ color: "red", marginBottom: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <TextField
        label="Full Name"
        variant="outlined"
        fullWidth
        value={fullName}
        onChange={(e) => {
          setFullName(e.target.value);
          setErrors({ ...errors, fullName: "" }); // Clear error
        }}
        error={!!errors.fullName}
        helperText={errors.fullName}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        onKeyUp={handleKeyPress}
      />
      <TextField
        label="Email Address"
        variant="outlined"
        fullWidth
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setErrors({ ...errors, email: "" }); // Clear error
        }}
        error={!!errors.email}
        helperText={errors.email}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        autoComplete="email"
        onKeyUp={handleKeyPress}
      />
      <TextField
        label="Company Name"
        variant="outlined"
        fullWidth
        value={companyName}
        onChange={(e) => {
          setCompanyName(e.target.value);
          setErrors({ ...errors, companyName: "" }); // Clear error
        }}
        error={!!errors.companyName}
        helperText={errors.companyName}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        autoComplete="off"
        onKeyUp={handleKeyPress}
      />
      <TextField
        label="Phone Number"
        variant="outlined"
        fullWidth
        value={phoneNumber}
        onChange={(e) => {
          setPhoneNumber(e.target.value);
          setErrors({ ...errors, phoneNumber: "" }); // Clear error
        }}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        autoComplete="tel"
        onKeyUp={handleKeyPress}
      />
      <TextField
        label="Password"
        variant="outlined"
        type={showPassword ? "text" : "password"}
        fullWidth
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setErrors({ ...errors, password: "" }); // Clear error
        }}
        error={!!errors.password}
        helperText={errors.password}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        autoComplete="new-password"
        onKeyUp={handleKeyPress}
      />
      <TextField
        label="Confirm Password"
        variant="outlined"
        type={showConfPassword ? "text" : "password"}
        fullWidth
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setErrors({ ...errors, confirmPassword: "" }); // Clear error
        }}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        sx={{
          marginBottom: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfPassword(!showConfPassword)}
              >
                {showConfPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        autoComplete="new-password"
        onKeyUp={handleKeyPress}
      />
      <Button
        style={signup_style.signupBtn}
        variant="contained"
        fullWidth
        onClick={handleSignup}
        disabled={loading}
        sx={{ marginBottom: 0 }}
      >
        {loading ? "Signing up..." : "Create Account"}
      </Button>
    </Box>
  );
};

export default SignupForm;
